import { useMemo, useState } from 'react'
import { Stack, Button, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useContactsSelected } from '../../../store'
import { useBatchOperations } from '../../../use-batch-operations'
import ActionModal, { ActionModalVariant } from 'components/Modal/ActionModal'

interface Contact {
  sf_id: string
  sync: boolean
  state: 'active' | 'deactivated' | 'unconfirmed' | 'locked'
  center_user_id?: string
}

interface ActionButtonConfig {
  tooltipKey: string
  color: string
  borderColor: string
  icon: string
  action: () => Promise<void>
  text?: string
  variant: ActionModalVariant
}

interface ActionButtonsProps {
  onRefetch?: () => void
}

const ActionButtons = ({ onRefetch }: ActionButtonsProps) => {
  const { t } = useTranslation()
  const [pendingAction, setPendingAction] = useState<ActionButtonConfig | null>(null)
  const [contactsSelected] = useContactsSelected()
  const {
    activateUsers,
    deactivateUsers,
    resendConfirmations,
    isLoading
  } = useBatchOperations({ onRefetch })

  const contactCounts = useMemo(() => {
    const syncedContacts = (contactsSelected as Contact[]).filter(contact => contact.sync)
    const activeContacts = syncedContacts.filter(contact => contact.state === 'active')
    const deactivatedContacts = syncedContacts.filter(contact => contact.state === 'deactivated')
    const unconfirmedContacts = syncedContacts.filter(contact => contact.state === 'unconfirmed')

    return {
      total: contactsSelected.length,
      synced: syncedContacts.length,
      active: activeContacts.length,
      deactivated: deactivatedContacts.length,
      unconfirmed: unconfirmedContacts.length
    }
  }, [contactsSelected])

  const buttonConfigs = useMemo((): ActionButtonConfig[] => {
    const configs: ActionButtonConfig[] = []

    if (contactCounts.active > 0) {
      configs.push({
        tooltipKey: 'auth.desactivate',
        color: '#DC2626',
        borderColor: '#FEE2E2',
        icon: 'fa-regular fa-pause',
        action: deactivateUsers,
        variant: 'error'
      })
    }

    if (contactCounts.deactivated > 0) {
      configs.push({
        tooltipKey: 'auth.activate',
        color: '#16A34A',
        borderColor: '#E6F0D7',
        icon: 'fa-regular fa-play',
        action: activateUsers,
        variant: 'success'
      })
    }

    if (contactCounts.unconfirmed > 0) {
      configs.push({
        tooltipKey: 'auth.detail.send_mail_button',
        color: '#374151',
        borderColor: '#E5E7EB',
        icon: 'fa-regular fa-paper-plane',
        action: resendConfirmations,
        text: 'auth.detail.send_mail_button',
        variant: 'primary'
      })
    }

    return configs
  }, [contactCounts, activateUsers, deactivateUsers, resendConfirmations])

  if (contactCounts.total === 0) return null

  return (
    <>
      <Stack direction="row" spacing={1}>
        {buttonConfigs.map((config, index) => {
          const buttonSx = {
            width: config.text ? 'auto' : '40px',
            height: '32px',
            minWidth: config.text ? 'auto' : '40px',
            color: config.color,
            borderColor: config.borderColor,
            backgroundColor: 'transparent',
            fontSize: config.text ? '12px' : 'inherit',
            '&:hover, &:focus, &:active': {
              borderColor: config.borderColor,
              backgroundColor: 'transparent',
              color: config.color,
            }
          }

          const buttonContent = isLoading ? (
            <i
              className="fa-regular fa-spinner fa-spin"
              style={{
                fontSize: config.text ? '10px' : '12px',
                marginRight: config.text ? '6px' : '0'
              }}
            />
          ) : (
            <i
              className={config.icon}
              style={{
                fontSize: config.text ? '10px' : '12px',
                marginRight: config.text ? '6px' : '0'
              }}
            />
          )

          return (
            <Tooltip key={index} title={t(config.tooltipKey)} arrow>
              <Button
                variant="outlined"
                size="small"
                disabled={isLoading}
                onClick={() => setPendingAction(config)}
                sx={buttonSx}
              >
                {buttonContent}
                {config.text && t(config.text, 'Renvoyer email')}
              </Button>
            </Tooltip>
          )
        })}
      </Stack>

      {pendingAction && (
        <ActionModal
          cancelLabel={t('ui_elements.cancel')}
          confirmLabel={t('ui_elements.confirm')}
          icon={<i className={pendingAction.icon} />}
          loading={isLoading}
          onClose={() => setPendingAction(null)}
          onConfirm={async () => {
            await pendingAction.action()
            setPendingAction(null)
          }}
          title={t(pendingAction.tooltipKey)}
          variant={pendingAction.variant}
        />
      )}
    </>
  )
}

export default ActionButtons
